/**
 * Downloads a zip of study user documents (PDFs) for the staging area.
 * Parity with legacy ApiEtmf.getStudyUserDocsDownload
 * @param options - Context options (study, environment, etc)
 * @param esignature - E-signature payload (optional)
 * @returns Promise resolving to download info
 */
export async function getStudyUserDocsDownload(
  options: Record<string, any>,
  esignature?: Record<string, any>
): Promise<{ url?: string; s3Url?: string; message?: string }> {
  // POST to /api/etmf/studyuserdocs/download
  const body = { ...options, esignature }
  if (options.selectedWholeResult) {
    // Strict parity: GET /api/etmf/studyuser/documents/{envFsiId}/downloadall with filter as query params
    return baseApiService.fetch<any>(`/api/etmf/studyuser/documents/${options.filter.envFsiId}/downloadall`, {
      params: options.filter,
      method: 'GET'
    })
  } else {
    // Strict parity: PATCH /api/files/multiDownload with {ids, esignature}
    return baseApiService.fetch<any>(`/api/files/multiDownload`, {
      method: 'PATCH',
      body: { ids: options.selectedIds, esignature }
    })
  }
}

/**
 * Downloads a zip of site user documents (PDFs) for the staging area.
 * Parity with legacy ApiEtmf.getSiteUserDocsDownload
 * @param options - Context options (site, environment, etc)
 * @param esignature - E-signature payload (optional)
 * @returns Promise resolving to download info
 */
export async function getSiteUserDocsDownload(
  options: Record<string, any>,
  esignature?: Record<string, any>
): Promise<{ url?: string; s3Url?: string; message?: string }> {
  // POST to /api/etmf/siteuserdocs/download
  const body = { ...options, esignature }
  if (options.selectedWholeResult) {
    // Strict parity: GET /api/etmf/siteuser/documents/{siteFsiId}/downloadall with filter as query params
    return baseApiService.fetch<any>(`/api/etmf/siteuser/documents/${options.filter.siteFsiId}/downloadall`, {
      params: options.filter,
      method: 'GET'
    })
  } else {
    // Strict parity: PATCH /api/files/multiDownload with {ids, esignature}
    return baseApiService.fetch<any>(`/api/files/multiDownload`, {
      method: 'PATCH',
      body: { ids: options.selectedIds, esignature }
    })
  }
}
/**
 * Removes a document from a group (FSI group) with optional e-signature validation.
 * Matches legacy ApiEtmf.removeDocFromGroup
 * @param fsiId - FSI group ID
 * @param esignature - E-signature object (optional)
 * @param hideLoader - If true, disables loader (default: true)
 * @returns Promise<any>
 */
export const removeDocFromGroup = async (
  fsiId: string | number,
  esignature?: Record<string, any>,
  hideLoader: boolean = true
): Promise<any> => {
  const baseUrl = '/api/etmf/fsi_group/' + fsiId + '/removedocument'
  const options = {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json;charset=UTF-8' },
    body: esignature ? JSON.stringify(esignature) : undefined,
    showLoader: !hideLoader
  }
  // Use baseApiService.fetch for consistency
  // Note: showLoader is not a standard fetch option, but included for parity with legacy
  // If your baseApiService supports it, pass it; otherwise, ignore
  return baseApiService.fetch<any>(baseUrl, options)
}
import { reduce, map, extend, isArray, where, filter } from 'underscore'
import { baseApiService } from './base'
import { getUserId } from '../auth'
import type { FilterOption } from '../../components/FiltersPanel/types'
import type { MasterDataTypeValue } from '../../constants/etmf'

/**
 * Parameters for getClinicalFolderIdByParams API call.
 * Used to retrieve the clinical folder ID based on hierarchical ETMF parameters.
 */
export interface ClinicalFolderParams {
  productValue?: string | number
  trialValue?: string | number
  environmentValue?: string | number
  countryValue?: string | number
  siteValue?: string | number
  siteId?: string | number
  zone?: string
  section?: string
  [key: string]: any
}

/**
 * Response from getClinicalFolderIdByParams API call.
 */
export interface ClinicalFolderResponse {
  id?: number | string
  [key: string]: any
}

/**
 * Item in the "Add to Group" FSI list.
 */
export interface GroupFsiItem {
  id: number | string
  name: string
  [key: string]: any
}
/*
 * Fetch FSI IDs from clinical UUIDs (for checkmate integration)
 * @param uuids - Array of clinical UUIDs
 * @returns Promise resolving to array of FSI objects with IDs
 */
export const getFsiIdsFromClinicalUuids = async (
  uuids: string[]
): Promise<Array<{ fsiId: number; uuid: string; mccObjType?: string; app?: string }>> => {
  return baseApiService.fetch<Array<{ fsiId: number; uuid: string; mccObjType?: string; app?: string }>>(
    '/api/etmf/mccdatausage/',
    {
      params: { mccObjUuids: uuids }
    }
  )
}
/*
 * Sorts an array of objects by their 'fsiName' property (case-insensitive, locale-aware).
 * Legacy: context.service.etmf.sortByFsiName
 * @param arr - Array of objects with 'fsiName' property
 * @returns Sorted array
 */
export function sortByFsiName<T extends { fsiName?: string }>(arr: T[]): T[] {
  return [...arr].sort((a, b) => {
    const aName = (a.fsiName || '').toLocaleLowerCase()
    const bName = (b.fsiName || '').toLocaleLowerCase()
    return aName.localeCompare(bName)
  })
}

/**
 * Fetches selection options for package application (cascading dropdowns).
 * Legacy: context.api.etmf.getSelectionsForPackage
 * @param level - The entity level (e.g., 'PRODUCT', 'STUDY', 'ENVIRONMENT', 'COUNTRY', 'SITE')
 * @param fsiId - Parent selection FSI ID (optional, for cascading)
 * @param filePlanId - File plan ID filter (optional)
 * @param isSilent - If true, disables loader (default: true)
 * @returns Promise resolving to array of {fsiId, fsiName}
 */
export async function getSelectionsForPackage(
  level: string,
  fsiId?: string | number | null,
  filePlanId?: string | number | null,
  isSilent: boolean = true
): Promise<Array<{ fsiId: string | number; fsiName: string }>> {
  const params: Record<string, any> = {}

  if (fsiId !== null && fsiId !== undefined) {
    params.fsiId = fsiId
  }

  if (filePlanId !== null && filePlanId !== undefined) {
    params.fpId = filePlanId
  }

  const response = await baseApiService.fetch<Array<{ id: string | number; name: string }>>(
    `/api/etmf/overview/selection/${level}`,
    { params }
  )

  return Array.isArray(response) ? response.map((item) => ({ fsiId: item.id, fsiName: item.name })) : []
}

/**
 * Fetches selection options for the staging area filter (studyuser selection by level).
 * Parity with legacy AngularJS getSelectionsForStagingAreaFilter.
 * @param level - The entity level (e.g., 'PRODUCT', 'STUDY', 'ENVIRONMENT', 'COUNTRY', 'SITE')
 * @param envFsiId - The environment FSI ID (contextId)
 * @param fsiIds - Array of FSI IDs to filter by (optional)
 * @param isSilent - If true, disables loader (default: true)
 * @returns Promise resolving to array of {fsiId, fsiName}
 */
export const getSelectionsForStagingAreaFilter = async (
  level: string,
  envFsiId: string | number,
  fsiIds?: string | number | Array<string | number>,
  isSilent: boolean = true
): Promise<Array<{ fsiId: string | number; fsiName: string }>> => {
  // Normalize fsiIds to string[] if present
  let fsiIdsArr: string[] = []
  if (Array.isArray(fsiIds)) {
    fsiIdsArr = fsiIds.map(String)
  } else if (fsiIds !== undefined && fsiIds !== null) {
    fsiIdsArr = [String(fsiIds)]
  }
  const params = fsiIdsArr.length > 0 && fsiIdsArr[0] ? { fsiIds: fsiIdsArr } : {}
  // Loader logic: showLoader = !isSilent
  // Endpoint: /api/etmf/studyuser/:envFsiId/selection/:level
  const response = await baseApiService.fetch<any[]>(`/api/etmf/studyuser/${envFsiId}/selection/${level}`, { params })
  // Legacy maps .plain() to array of {fsiId, fsiName}
  return Array.isArray(response) ? response.map((item: any) => ({ fsiId: item.id, fsiName: item.name })) : []
}

/**
 * Fetches property values for a given metadata template ID (for dropdowns like zone, section, milestone, artifact).
 * Matches legacy AngularJS API usage.
 * @param templateId - The metadata template ID
 * @param params - Optional query parameters
 * @param isSilent - If true, disables loader (default: true)
 * @returns Promise resolving to an array of property values
 */
export const propertyValuesSearch = async (
  templateId: string | number,
  params: Record<string, any> = {},
  isSilent: boolean = true
): Promise<any[]> => {
  // showLoader: !isSilent (legacy: showLoader false if isSilent true)
  const showLoader = !isSilent
  return baseApiService.fetch<any[]>(`/api/etmf/metadata/${templateId}/values`, {
    params
  })
}
/**
 * Property search for ETMF (staging area).
 * Matches legacy AngularJS API usage.
 * @param envFsiId - The environment FSI ID (contextId)
 * @param meta - Meta/pagination/sort params
 * @param request - Request object containing filter and optional tag
 * @returns Promise resolving to the API response
 */
export const propertySearch = async (
  envFsiId: string | number,
  meta: Record<string, any> = {},
  request: { filter: Record<string, any>; tag?: string }
): Promise<{ elements: any[]; hasNextPage: boolean; totalElements: number }> => {
  const reqObj: any = { filter: request.filter, meta }
  if (request.tag) reqObj.tag = request.tag
  return baseApiService.fetch<{ elements: any[]; hasNextPage: boolean; totalElements: number }>(
    `/api/etmf/propertysearch/${envFsiId}`,
    {
      method: 'POST',
      body: reqObj
    }
  )
}
/**
 * Fetches study user documents for a given envFsiId and query/filter params.
 * Matches legacy AngularJS API usage.
 * @param envFsiId - The environment FSI ID (contextId)
 * @param params - Query parameters (pagination, sorting, etc)
 * @param filter - Filter parameters (fsiIds, countriesfsiIds, etc)
 * @returns Promise resolving to the API response
 */
export const getStudyUserDocs = async (
  envFsiId: string | number,
  params: Record<string, any> = {},
  filter: Record<string, any> = {}
): Promise<{ elements: any[]; totalElements: number; hasNextPage?: boolean }> => {
  // Merge params and filter, as in legacy
  const queryParams = { ...params, ...filter, envFsiId }
  return baseApiService.fetch<{ elements: any[]; totalElements: number; hasNextPage?: boolean }>(
    `/api/etmf/studyuser/documents/${envFsiId}`,
    { params: queryParams }
  )
}
/**
 * Fetches site user documents for a given siteFsiId and query/filter params.
 * Matches legacy AngularJS API usage.
 * @param siteFsiId - The site FSI ID (contextId)
 * @param params - Query parameters (pagination, sorting, etc)
 * @param filter - Filter parameters (fsiIds, countriesfsiIds, etc)
 * @returns Promise resolving to the API response
 */
export const getSiteUserDocs = async (
  siteFsiId: string | number,
  params: Record<string, any> = {},
  filter: Record<string, any> = {}
): Promise<{ elements: any[]; totalElements: number; hasNextPage?: boolean }> => {
  // Merge params and filter, as in legacy
  const queryParams = { ...params, ...filter, siteFsiId }
  return baseApiService.fetch<{ elements: any[]; totalElements: number; hasNextPage?: boolean }>(
    `/api/etmf/siteuser/documents/${siteFsiId}`,
    { params: queryParams }
  )
}

/**
 * Filter structure for clinical report API request
 */
export interface ClinicalReportFilter {
  action?: string[]
  createdAtFrom?: string | null
  createdAtTo?: string | null
  createdById?: string[]
  includeSystemGeneratedEvents?: boolean
  type?: 'STUDY' | 'SITE'
  product?: any
  study?: any
  environment?: any
  country?: any
  site?: any
}

/**
 * Master data response type
 */
export interface MasterDataResponse {
  elements?: any[]
  [key: string]: any
}

/**
 * Master data item type (used in ETMF list views)
 */
export interface MasterDataItem {
  id: number | string
  name: string
  isMDF?: boolean
  MDFType?: string
  [key: string]: any
}

/**
 * File plan item type (used in ETMF list views)
 */
export interface FilePlanItem {
  id: number | string
  name: string
  [key: string]: any
}

/**
 * Meta/pagination parameters for clinical report API request
 */
export interface ClinicalReportMeta {
  pageStart: number
  pageSize: number
  sortBy: string
  sortDesc: boolean
  userId?: number
}

/**
 * Individual activity log entry in clinical report response
 */
export interface ClinicalActivityLogEntry {
  id: number
  createdById: number
  createdAt: string
  action: string
  detail?: any
}
// --- Type added for dropdown API responses (matches legacy usage) ---
export interface EtmfDropdownResponse {
  id: number | string
  name?: string
  propertyValue?: string
  label?: string
}
// --- Type added for clinical report API response (minimal, expand as needed) ---
export interface ClinicalReportResponse {
  elements: ClinicalActivityLogEntry[]
  totalElements: number
  pageSize: number
  pageStart: number
  [key: string]: any
}
export interface EtmfSelectionResponse {
  fsiId: number
  fsiName: string
  fsiPropName?: string
}

/**
 * Requests a document change workflow for FILE_ARTIFACT workflow type.
 * This creates a workflow with metadata updates in a single operation.
 *
 * @param workflowStartRequestDTO - The workflow start request DTO
 * @param metadatas - Array of metadata templates with values
 * @param options - Additional options (effective flag, groupTargetFsiId, retainOriginal)
 * @returns Promise resolving to the workflow response
 */
export async function requestDocumentChangeV2(
  workflowStartRequestDTO: any,
  metadatas: Array<{ metadataTemplateId: number; value: any }>,
  options: { effective?: boolean; groupTargetFsiId?: number; retainOriginal?: boolean }
): Promise<any> {
  const userId = getUserId()

  return baseApiService.fetch('/api/etmf/filedocument', {
    method: 'POST',
    body: {
      workflowStartRequestDTO,
      userId,
      modifyMetadatasRequestDTO: { metadata: metadatas },
      ...options
    }
  })
}

/**
 * Requests a multi-target document change workflow for FILE_ARTIFACT workflow type.
 * Used when isMultiTargetEtmfFileMode=true to file a document to multiple destinations in a single CR request.
 *
 * @param workflowStartRequestDTO - The workflow start request DTO (uses targetFsiIds instead of targetFsiId)
 * @param metadatas - Array of metadata templates with values
 * @param options - Additional options (effective flag, retainOriginal)
 * @returns Promise resolving to the workflow response
 */
export async function requestDocumentChangeMulti(
  workflowStartRequestDTO: any,
  metadatas: Array<{ metadataTemplateId: number; value: any }>,
  options: {
    effective?: boolean
    retainOriginal?: boolean
    targetFsiIds: number[]
    sourceFsiId?: number | string
    documentTypeTemplateId?: number | string
    fileName?: string
  }
): Promise<any> {
  const userId = getUserId()

  return baseApiService.fetch('/api/etmf/filedocument/multi', {
    method: 'POST',
    body: {
      workflowStartRequestDTO,
      userId,
      modifyMetadatasRequestDTO: { metadata: metadatas },
      ...options
    }
  })
}

// Staging-related interfaces
export interface LinkedEntitiesResponse {
  entityLinks: Array<{
    category: string
    label: string
    linkedEntityId: string | number
    linkedEntityName?: string
    required?: boolean
    type: string
  }>
  entityLinkMap: {
    SITE: string | number
    TRIAL: string | number
    SITE_ADDRESS: string | number
    SITE_NUMBER: string | number
    FILE_PLAN_FOLDER: string | number
    [key: string]: string | number
  }
  editable?: boolean
}

export interface EtmfCompletionRateDTO extends Record<string, unknown> {
  siteId?: number | null
  studyId?: number | number[] | null
  studyIds?: number[]
  userIds?: number[]
  completionType?: 'COMPLETED' | 'MISSING' | null
  tagIds?: number[]
  formatToPercentageValue?: boolean
  reportType?: string
  includeStagingAreaDocs?: boolean
  metadataCriteria?: Array<{
    metadataTemplateId: number
    condition: string
    includeInherited: boolean
    value: any
  }>
  filterId?: number
}

/**
 * Interface for report meta/pagination parameters.
 */
export interface EtmfReportMeta {
  pageStart: number
  pageSize: number
  sortBy: string
  sortDesc: boolean
}

/**
 * Request body for getCompletedAndMissingDocsReport API
 */
export interface CompletedAndMissingDocsReportRequest {
  rateDTO: EtmfCompletionRateDTO
  completionType: 'COMPLETED' | 'MISSING' | null
  meta: EtmfReportMeta
  [key: string]: unknown
}

/**
 * Request body for exportCompletedAndMissingDocsReport API
 */
export interface ExportCompletedAndMissingDocsReportRequest {
  reportType: string
  rateDTO: EtmfCompletionRateDTO
  completionType: 'COMPLETED' | 'MISSING' | null
  filterId?: number
  [key: string]: unknown
}

export interface SiteProperties {
  [key: string]: string
}

export interface SiteFolderEmailResponse {
  email: string
}

export interface SiteUploadFolderResponse {
  id: string | number
}

/**
 * Placeholder item returned from the API.
 * Used for selecting placeholders for filing documents.
 */
export interface PlaceholderItem {
  id: string | number
  name: string
  [key: string]: unknown
}

/**
 * Fetches placeholders for a given ETMF section and artifact name.
 * Used in request-change workflow when selecting placeholder filing options.
 * Parity with legacy AngularJS getPlaceholderForSectionAndArtifactName.
 * @param sectionId - The ETMF section FSI ID (optional)
 * @param artifactName - The artifact name/type (optional)
 * @param isSilent - If true, disables loader (default: true)
 * @returns Promise resolving to array of {id, name} placeholder objects
 */

export const getPlaceholderForSectionAndArtifactName = async (
  sectionId?: string | number,
  artifactName?: string,
  isSilent: boolean = true
): Promise<PlaceholderItem[]> => {
  const params: Record<string, string | number> = {}
  if (sectionId) params.sectionFsiId = sectionId
  if (artifactName) params.artifact = artifactName

  const response = await baseApiService.fetch<PlaceholderItem[]>('/api/v1/etmf/overview/selection/placeholders', {
    params
  })

  return response
}

export const getProducts = async (): Promise<FilterOption[]> => {
  try {
    const response = await baseApiService.fetch<EtmfDropdownResponse[]>(
      '/api/etmf/overview/dropdown/includeproperty/PRODUCT'
    )

    return response.map((item) => ({
      label: item.name ?? item.propertyValue ?? `Product ${item.id}`,
      value: String(item.id)
    }))
  } catch (error) {
    console.error('Error fetching products:', error)
    return []
  }
}

export const getStudies = async (productId: string | string[]): Promise<FilterOption[]> => {
  try {
    const fsiId = Array.isArray(productId) ? productId[0] : productId

    const response = await baseApiService.fetch<EtmfDropdownResponse[]>(
      '/api/etmf/overview/dropdown/includeproperty/STUDY',
      {
        params: { fsiId }
      }
    )

    if (!Array.isArray(response)) {
      console.error('Expected array response but got:', typeof response, response)
      return []
    }

    const mappedResponse = response
      .map((item, index) => {
        const label = item.name ?? item.propertyValue ?? item.label ?? `Option ${index + 1}`
        const value = String(item.id || index)

        return {
          label: String(label),
          value: String(value)
        }
      })
      .filter((option) => option.label && option.value) // Filter out any invalid options

    return mappedResponse
  } catch (error) {
    console.error('Error fetching studies:', error)
    return []
  }
}

export const getEnvironments = async (studyId: string | string[]): Promise<FilterOption[]> => {
  try {
    // For multi-select study values, we need special handling
    // For now, if multiple studies are selected, return empty (as per business requirement)
    if (Array.isArray(studyId) && studyId.length > 1) {
      console.log('Multiple studies selected - environments disabled')
      return []
    }

    const fsiId = Array.isArray(studyId) ? studyId[0] : studyId

    const response = await baseApiService.fetch<EtmfDropdownResponse[]>(
      '/api/etmf/overview/dropdown/includeproperty/ENVIRONMENT',
      {
        params: { fsiId }
      }
    )

    return response.map((item) => ({
      label: item.name ?? item.propertyValue ?? `Environment ${item.id}`,
      value: String(item.id)
    }))
  } catch (error) {
    console.error('Error fetching environments:', error)
    return []
  }
}

export const getCountries = async (environmentId: string | string[]): Promise<FilterOption[]> => {
  try {
    const fsiId = Array.isArray(environmentId) ? environmentId[0] : environmentId

    const response = await baseApiService.fetch<EtmfDropdownResponse[]>(
      '/api/etmf/overview/dropdown/includeproperty/COUNTRY',
      {
        params: { fsiId }
      }
    )

    return response.map((item) => ({
      label: item.name ?? item.propertyValue ?? `Country ${item.id}`,
      value: String(item.id)
    }))
  } catch (error) {
    console.error('Error fetching countries:', error)
    return []
  }
}

export const getSites = async (countryId: string | string[]): Promise<FilterOption[]> => {
  try {
    const fsiId = Array.isArray(countryId) ? countryId[0] : countryId

    const response = await baseApiService.fetch<EtmfDropdownResponse[]>(
      '/api/etmf/overview/dropdown/includeproperty/SITE',
      {
        params: { fsiId }
      }
    )

    return response.map((item) => ({
      label: item.name ?? item.propertyValue ?? `Site ${item.id}`,
      value: String(item.id)
    }))
  } catch (error) {
    console.error('Error fetching sites:', error)
    return []
  }
}

// Staging-related functions (consolidated from staging.ts)
export const getLinkedEntities = async (): Promise<LinkedEntitiesResponse> => {
  return baseApiService.fetch<LinkedEntitiesResponse>('/api/etmf/linkedentities')
}
/**
 * Fetches site properties, optionally filtered by propertyTypes (legacy: passes as id param)
 */
export const getSiteProperties = async (siteId: string | number, propertyTypes: string[]): Promise<SiteProperties> => {
  const params = Array.isArray(propertyTypes) && propertyTypes.length > 0 ? { id: propertyTypes } : undefined
  return baseApiService.fetch<SiteProperties>(`/api/etmf/properties/${siteId}`, params ? { params } : undefined)
}

export const getSiteFolderEmailAddress = async (siteId: string | number): Promise<SiteFolderEmailResponse> => {
  return baseApiService.fetch<SiteFolderEmailResponse>(`/api/etmfemail/${siteId}`)
}

export const getSiteUploadFolderId = async (contextId: string | number): Promise<SiteUploadFolderResponse> => {
  return baseApiService.fetch<SiteUploadFolderResponse>(`/api/etmf/uploadedfolderid/${contextId}`)
}

// My Environments/Sites/Studies APIs
export interface MyEnvironmentsFilter {
  pageSize?: number
  pageStart?: number
  userId?: number | string
  name?: string
  fsiIds?: number[]
  sortBy?: string
  sortDesc?: boolean
  [key: string]: string | number | boolean | number[] | undefined
}

export interface MyEnvironmentsResponse {
  elements: Array<{
    fsiId: number
    studyName: string
    envName: string
    [key: string]: unknown
  }>
  totalElements: number
  pageSize: number
  pageStart: number
}

export interface MySitesResponse {
  elements: Array<{
    fsiId: number
    siteName: string
    [key: string]: unknown
  }>
  totalElements: number
  pageSize: number
  pageStart: number
}

export interface MyStudiesResponse {
  elements: Array<{
    fsiId: number
    studyName: string
    [key: string]: unknown
  }>
  totalElements: number
  pageSize: number
  pageStart: number
}

/**
 * Get user's environments (study + environment combinations)
 * @param filter - Filter parameters including userId, pagination, name search
 */
export const getMyEnvironments = async (filter?: MyEnvironmentsFilter): Promise<MyEnvironmentsResponse> => {
  const params = {
    pageSize: 10,
    pageStart: 0,
    userId: getUserId(),
    ...filter
  }

  return baseApiService.fetch<MyEnvironmentsResponse>('/api/etmf/folderstructures/environments', {
    params
  })
}

/**
 * Get user's sites
 * @param filter - Filter parameters including userId, pagination, name search
 * @param isSilent - If true, disables loader (default: true)
 */
export const getMySites = async (filter?: MyEnvironmentsFilter, isSilent: boolean = true): Promise<MySitesResponse> => {
  const userId = getUserId()
  const params = {
    pageSize: 10,
    pageStart: 0,
    ...filter,
    userId
  }
  // If baseApiService supports loader control, pass it here (pseudo-code):
  // return baseApiService.fetch<MySitesResponse>('/api/etmf/folderstructures/sites', { params, showLoader: !isSilent })
  return baseApiService.fetch<MySitesResponse>('/api/etmf/folderstructures/sites', {
    params
  })
}

/**
 * Get user's studies (trials)
 * @param filter - Filter parameters including userId, pagination, name search
 */
export const getMyStudies = async (filter?: MyEnvironmentsFilter): Promise<MyStudiesResponse> => {
  const userId = getUserId()
  const params = {
    pageSize: 10,
    pageStart: 0,
    ...filter,
    userId
  }

  return baseApiService.fetch<MyStudiesResponse>('/api/etmf/folderstructures/trials', {
    params
  })
}

/**
 * Get environment options for infinite scroll picker
 * Used in filter dropdowns with async pagination
 * @param search - Search term for filtering by name
 * @param page - Page number for pagination (0-indexed)
 * @param userId - User ID for filtering results
 * @returns Promise with options array and hasMore flag
 */
export const getOptionsForAsyncPickerInfiniteScrollMyEnvironments = async (
  search: string | null,
  page: number,
  userId: string
): Promise<{ options: FilterOption[]; hasMore: boolean }> => {
  const pageSize = 10
  const params = {
    pageStart: page,
    userId,
    pageSize,
    name: search || ''
  }

  const response = await baseApiService.fetch<MyEnvironmentsResponse>('/api/etmf/folderstructures/environments', {
    params
  })

  const hasMore = (page + 1) * pageSize < response.totalElements

  return {
    options: response.elements.map((env) => ({
      value: env.fsiId.toString(),
      label: `${env.studyName} (${env.envName})`
    })),
    hasMore
  }
}

/**
 * Get study options for infinite scroll picker
 * Used in filter dropdowns with async pagination
 * @param search - Search term for filtering by name
 * @param page - Page number for pagination (0-indexed)
 * @param userId - User ID for filtering results
 * @returns Promise with options array and hasMore flag
 */
export const getOptionsForAsyncPickerInfiniteScrollMyStudies = async (
  search: string | null,
  page: number,
  userId: string
): Promise<{ options: FilterOption[]; hasMore: boolean }> => {
  const pageSize = 10
  const params = {
    pageStart: page,
    userId,
    pageSize,
    name: search || ''
  }

  const response = await baseApiService.fetch<MyStudiesResponse>('/api/etmf/folderstructures/trials', { params })

  const hasMore = (page + 1) * pageSize < response.totalElements

  return {
    options: response.elements.map((study) => ({
      value: study.fsiId.toString(),
      label: study.studyName
    })),
    hasMore
  }
}

/**
 * Get site options for infinite scroll picker
 * Used in filter dropdowns with async pagination
 * @param search - Search term for filtering by name
 * @param page - Page number for pagination (0-indexed)
 * @param userId - User ID for filtering results
 * @returns Promise with options array and hasMore flag
 */
export const getOptionsForAsyncPickerInfiniteScrollMySites = async (
  search: string | null,
  page: number,
  userId: string
): Promise<{ options: FilterOption[]; hasMore: boolean }> => {
  const pageSize = 10
  const params = {
    pageStart: page,
    userId,
    pageSize,
    name: search || ''
  }

  const response = await baseApiService.fetch<MySitesResponse>('/api/etmf/folderstructures/sites', { params })

  const hasMore = (page + 1) * pageSize < response.totalElements

  return {
    options: response.elements.map((site) => ({
      value: site.fsiId.toString(),
      label: site.siteName
    })),
    hasMore
  }
}

/**
 * Get selections for ETMF reports with property values
 * Used for cascading dropdowns in Clinical Activity Reports
 * @param level - The entity level (e.g., 'PRODUCT', 'STUDY', 'ENVIRONMENT', 'COUNTRY', 'SITE')
 * @param fsiId - Optional parent FSI ID to filter by
 * @param isSilent - Optional flag to suppress loading indicator (not used in new architecture, kept for API compatibility)
 */
export const getSelectionsForEtmfReports = async (
  level: string,
  fsiId?: number | string,
  isSilent = true
): Promise<EtmfSelectionResponse[]> => {
  try {
    const params: Record<string, string | number> = {}
    if (fsiId) {
      params.fsiId = fsiId
    }

    const response = await baseApiService.fetch<Array<{ id: number; name: string; propertyValue?: string }>>(
      `/api/etmf/overview/dropdown/includeproperty/${level}`,
      {
        params
      }
    )

    return response.map((item) => ({
      fsiId: item.id,
      fsiName: item.name,
      fsiPropName: item.propertyValue
    }))
  } catch (error) {
    console.error(`Error fetching selections for ${level}:`, error)
    return []
  }
}

/**
 * Get clinical activity report data
 * @param filter - Clinical report filter criteria (actions, dates, hierarchy selections)
 * @param meta - Pagination and sorting parameters
 * @returns Promise resolving to clinical report response with activity log entries
 */
export const getClinicalReport = async (
  filter: ClinicalReportFilter,
  meta: ClinicalReportMeta
): Promise<ClinicalReportResponse> => {
  const reqObj = { meta, filter }
  return baseApiService.fetch('/api/reports/clinicalreport', {
    method: 'POST',
    body: reqObj
  })
}

/**
 * Export clinical activity report
 * @param filter - Clinical report filter criteria
 * @param meta - Pagination and sorting parameters
 * @returns Promise resolving when export is initiated
 */
export const exportClinicalActivity = async (filter: ClinicalReportFilter, meta: ClinicalReportMeta): Promise<void> => {
  const reqObj = { filter, meta }
  return baseApiService.fetch('/api/reports/clinicalexport', {
    method: 'POST',
    body: reqObj
  })
}

/**
 * Update linked entities
 * @param links - Object mapping entity types to their linked entity IDs
 * @returns Promise<any>
 */
export const updateLinkedEntities = async (links: Record<string, number | string>): Promise<any> => {
  const reqObj = { links }
  return baseApiService.fetch('/api/etmf/linkedentities', {
    method: 'POST',
    body: reqObj
  })
}

// Staging-related functions (consolidated from staging.ts)
export const getEtmfLinkedEntities = async (): Promise<LinkedEntitiesResponse> => {
  return baseApiService.fetch<LinkedEntitiesResponse>('/api/etmf/linkedentities')
}

export const updateProductionEnv = async () => {
  return baseApiService.fetch('/api/etmf/updateproductionenvironment')
}

/**
 * Initializes ETMF with esignature validation (matches legacy AngularJS behavior)
 * @param validation - esignature validation object
 * @returns Promise<any> - resolves with the API response
 */
export const initEtmf = async (validation: any): Promise<any> => {
  return baseApiService.fetch('/api/etmf/initetmf', {
    method: 'POST',
    body: { esignature: validation }
  })
}

/**
 * Triggers Rave content generation (matches legacy AngularJS behavior)
 * @param validation - esignature validation object
 * @returns Promise<any> - resolves with the API response
 */
export const generateRaveContent = async (validation: any): Promise<any> => {
  return baseApiService.fetch('/api/etmf/initrcr', {
    method: 'POST',
    body: { esignature: validation }
  })
}

/**
 * Renames site folders (matches legacy AngularJS behavior)
 * @returns Promise<any> - resolves with the API response
 */
export const renameSiteFolders = async (): Promise<any> => {
  return baseApiService.fetch('/api/etmf/folderstructures/updatesitefolders', {
    method: 'POST'
  })
}

/**
 * Get ETMF completion status by level.
 * Used in ETMF Overview dashboard to fetch completion statistics.
 * Matches legacy: getStatusByLevel(level, fsiId, params, isSilent)
 *
 * @param level - The entity level (e.g., 'ALL', 'STUDY', 'COUNTRY', 'SITE')
 * @param fsiId - The FSI ID for the entity
 * @param params - Additional query parameters (milestone, rule, stage, tagIds, etc.)
 * @param isSilent - If true, disables loader (default: false)
 * @returns Promise resolving to completion status data
 */
export const getStatusByLevel = async (
  level: string,
  fsiId: number | string,
  params: Record<string, any> = {},
  isSilent: boolean = false
): Promise<any> => {
  const queryParams = {
    fsiId,
    ...params
  }

  return baseApiService.fetch<any>(`/api/etmf/overview/completion/${level}`, {
    params: queryParams
  })
}

export const getTimelinessStatus = async (
  params: Record<string, any> = {},
  isSilent: boolean = false
): Promise<any> => {
  return baseApiService.fetch<any>(`/api/v1/reports/etmfsummarytimeliness/count`, {
    params: params
  })
}

export const getQualityStatus = async (params: Record<string, any> = {}, isSilent: boolean = false): Promise<any> => {
  return baseApiService.fetch<any>(`/api/v1/reports/etmfsummarydocquality/count`, {
    params: params
  })
}

/**
 * Get study documents for overview using property-based search.
 * Used in ETMF Overview study documents table with advanced filtering.
 * Matches legacy: getStudyDocumentsForOverviewPropertyBased(search, params)
 *
 * @param search - Search criteria object containing filters (product, study, country, site, metadata, etc.)
 * @param params - Query parameters (pageSize, pageStart, sortBy, sortDesc, etc.)
 * @returns Promise resolving to study documents data with elements array
 */
export const getStudyDocumentsForOverviewPropertyBased = async (
  search: Record<string, any>,
  params: Record<string, any> = {}
): Promise<any> => {
  const queryParams = {
    ...search,
    ...params
  }

  return baseApiService.fetch<any>('/api/v1/etmf/overview/trialdocuments', {
    params: queryParams
  })
}

export const getStudyDocumentsForTimelinessPropertyBased = async (
  search: Record<string, any>,
  params: Record<string, any> = {}
): Promise<any> => {
  const queryParams = {
    ...search,
    ...params
  }

  return baseApiService.fetch<any>('/api/v1/reports/etmfsummarytimeliness', {
    params: queryParams
  })
}

export const getStudyDocumentsForQualityPropertyBased = async (
  search: Record<string, any>,
  params: Record<string, any> = {}
): Promise<any> => {
  const queryParams = {
    ...search,
    ...params
  }

  return baseApiService.fetch<any>('/api/v1/reports/etmfsummarydocquality', {
    params: queryParams
  })
}

/**
 * Export missing documents report using property-based search.
 * Used in ETMF Overview study documents table to export filtered results.
 * Matches legacy: exportMissingDocumentsPropertyBased(search)
 *
 * @param search - Search criteria object containing filters (product, study, country, site, metadata, etc.)
 * @returns Promise resolving to export response (typically triggers file download)
 */
export const exportMissingDocumentsPropertyBased = async (search: Record<string, any>): Promise<any> => {
  return baseApiService.fetch<any>('/api/v1/reports/trialdocuments/export', {
    method: 'POST',
    body: search
  })
}

/**
 * @param rateDTO - Report filter criteria
 * @param completionType - Filter by 'COMPLETED', 'MISSING', or null for ALL
 * @param meta - Pagination and sorting parameters
 * @returns Promise - Report data with documents
 */
export const getCompletedAndMissingDocsReport = async (
  rateDTO: EtmfCompletionRateDTO,
  completionType: 'COMPLETED' | 'MISSING' | null,
  meta: EtmfReportMeta
): Promise<any> => {
  const requestObj: CompletedAndMissingDocsReportRequest = {
    rateDTO,
    completionType,
    meta
  }
  return baseApiService.fetch('/api/reports/completionandmissingdocs', {
    method: 'POST',
    body: requestObj
  })
}

/**
 *
 * @param reportType - Report format type (typically 'XLSX')
 * @param rateDTO - Report filter criteria
 * @param completionType - Filter by 'COMPLETED', 'MISSING', or null for ALL
 * @param filterId - Optional saved filter ID
 * @returns Promise - Export confirmation
 */
export const exportCompletedAndMissingDocsReport = async (
  reportType: string,
  rateDTO: EtmfCompletionRateDTO,
  completionType: 'COMPLETED' | 'MISSING' | null,
  filterId?: number | null
): Promise<any> => {
  const requestObj: ExportCompletedAndMissingDocsReportRequest = {
    reportType,
    rateDTO,
    completionType
  }
  if (filterId) {
    requestObj.filterId = filterId
  }
  return baseApiService.fetch('/api/reports/completionandmissingdocs/export', {
    method: 'POST',
    body: requestObj
  })
}

/**
 * Get ETMF filed documents report data.
 * Used for ETMF_FINAL_DOCS report type with table rendering.
 * Matches legacy: getFiledDocsReport(reqDTO, meta)
 *
 * @param reqDTO - Report filter criteria
 * @param meta - Pagination and sorting parameters
 * @returns Promise<any> - Report data with filteredDocs
 */
export const getFiledDocsReport = async (reqDTO: EtmfCompletionRateDTO, meta: EtmfReportMeta): Promise<any> => {
  return baseApiService.fetch('/api/reports/finaldocs', {
    method: 'POST',
    body: {
      ...reqDTO,
      meta
    }
  })
}

/**
 * Export ETMF filed documents report to Excel.
 * Used for ETMF_FINAL_DOCS report type.
 * Matches legacy: exportFiledDocsReport(reqDTO)
 *
 * @param reqDTO - Report filter criteria with filterId
 * @returns Promise<any> - Export confirmation
 */
export const exportFiledDocsReport = async (reqDTO: EtmfCompletionRateDTO): Promise<any> => {
  return baseApiService.fetch('/api/reports/finaldocs/export', {
    method: 'POST',
    body: reqDTO
  })
}

/**
 * Export ETMF contributor document status report to Excel.
 * Used for ETMF_CONTRIBUTOR_DOC_STATUS report type.
 * Matches legacy: exportContributorDocStatusReport(reqDTO)
 *
 * @param reqDTO - Report filter criteria with userIds
 * @returns Promise<any> - Export confirmation
 */
export const exportContributorDocStatusReport = async (reqDTO: EtmfCompletionRateDTO): Promise<any> => {
  return baseApiService.fetch('/api/reports/contributordocstatus/export', {
    method: 'POST',
    body: reqDTO
  })
}

/**
 * Export ETMF document detail report to Excel.
 * Used for ETMF_DOCUMENT_DETAIL report type.
 * Matches legacy: exportDocumentDetailReport(reqDTO)
 *
 * @param reqDTO - Report filter criteria with completionType and tagIds
 * @returns Promise<any> - Export confirmation
 */
export const exportDocumentDetailReport = async (reqDTO: EtmfCompletionRateDTO): Promise<any> => {
  return baseApiService.fetch('/api/reports/etmfdocumentdetail/export', {
    method: 'POST',
    body: reqDTO
  })
}

/**
 * Fetches file plans (TypeScript migration of legacy getFilePlans)
 * @param params - Query parameters for file plans
 * @param isSilent - If true, disables loader
 * @returns Promise<any> - resolves with the API response
 */
export const getFilePlans = async (params: Record<string, any>, isSilent?: boolean): Promise<any> => {
  return baseApiService.fetch('/api/etmf/fileplans', {
    method: 'GET',
    params
  })
}

/**
 * Fetches file plans for picker (TypeScript migration of legacy getFilePlansForPicker)
 * @param params - Query parameters for file plan picker
 * @param hideLoader - If true, disables loader
 * @returns Promise<any> - resolves with the API response
 */
export const getFilePlansForPicker = async (params: any = {}, hideLoader?: boolean): Promise<any> => {
  return baseApiService.fetch('/api/etmf/fileplanpicklist', {
    method: 'GET',
    params
  })
}

/**
 * Fetches master data (TypeScript migration of legacy getMasterData).
 * Direct migration from AngularJS api.etmf.js (lines 788-804).
 *
 * @param type - Master data type (e.g., 'TRIAL', 'SITE', 'ENVIRONMENT', 'COUNTRY', 'PRODUCT')
 * @param params - Query parameters for master data request
 * @param isSilent - If true, disables the loading indicator (default: false)
 * @param isPicklist - If true, fetches picklist variant (affects endpoint and data transformation)
 * @returns Promise<MasterDataResponse> - Master data with elements array
 *
 * @example
 * // Get master data for trials
 * const trialData = await getMasterData('TRIAL', { pageSize: 20 })
 *
 * @example
 * // Get picklist data (no isMDF/MDFType added)
 * const picklist = await getMasterData('SITE', {}, false, true)
 *
 * Note: When isPicklist is false, adds isMDF=true and MDFType properties to each element
 */
export const getMasterData = async (
  type: string,
  params: Record<string, any> = {},
  isSilent: boolean = false,
  isPicklist?: boolean
): Promise<MasterDataResponse> => {
  // Legacy: new ApiBase({showLoader: !isSilent})
  // Note: baseApiService doesn't currently support showLoader control
  const endpoint = `/api/etmf/masterdata${isPicklist ? 'picklist' : ''}/${type}`
  const response = await baseApiService.fetch<MasterDataResponse>(endpoint, {
    method: 'GET',
    params
  })

  // Legacy behavior: If not picklist, add isMDF and MDFType to each element
  if (!isPicklist && response.elements) {
    response.elements.forEach((item: any) => {
      item.isMDF = true
      item.MDFType = type
    })
  }

  return response
}

/**
 * Generates trial structure (folder hierarchy) for ETMF based on the provided configuration.
 * Direct migration from AngularJS api.etmf.js (lines 774-779).
 *
 * This method creates the complete ETMF folder structure including zones, sections, and artifacts
 * for trials, countries, sites, and environments based on the selected file plan and configuration.
 *
 * @param data - Trial structure generation configuration containing:
 *   - trialFsiId: Trial folder structure instance ID
 *   - filePlanId: File plan template ID to use for structure
 *   - countryFsiIds: Array of country FSI IDs to generate structure for
 *   - siteFsiIds: Array of site FSI IDs to generate structure for
 *   - Additional configuration options
 * @param esignature - Electronic signature object for validation (optional)
 * @returns Promise<any> - Generation result with created folder structure information
 *
 * @example
 * const result = await generateTrialStructure({
 *   trialFsiId: 12345,
 *   filePlanId: 67890,
 *   countryFsiIds: [111, 222],
 *   siteFsiIds: [333, 444]
 * }, esignatureObj)
 *
 * Note: This is a critical operation that requires e-signature validation.
 * The endpoint uses POST /api/v1/etmf/generator
 */
export const generateTrialStructure = async (
  data: Record<string, any>,
  esignature?: Record<string, any>
): Promise<any> => {
  const requestObj = { ...data, esignature }

  return baseApiService.fetch<any>('/api/v1/etmf/generator', {
    method: 'POST',
    body: requestObj
  })
}

/**
 * Adds master data (trials, countries, sites) to existing master data in ETMF structure.
 * Direct migration from AngularJS api.etmf.js (lines 754-773).
 *
 * This method is used to associate master data entities (e.g., add countries to a trial,
 * or sites to a country) in the ETMF folder structure hierarchy.
 *
 * @param type - Master data type from Constants.etmf.masterData.type:
 *   - 'COUNTRY': Add countries to a trial
 *   - 'SITE': Add sites to a country
 *   - Default: 'TRIAL' (for other trial-level operations)
 * @param data - Master data configuration containing:
 *   - parentFsiId: Parent folder structure instance ID
 *   - childFsiIds: Array of child FSI IDs to add
 *   - Additional configuration
 * @param esignature - Electronic signature object for validation (optional)
 * @returns Promise<any> - Result of the add operation
 *
 * @example
 * // Add countries to a trial
 * await addMDtoMD('COUNTRY', {
 *   parentFsiId: 12345,
 *   childFsiIds: [111, 222, 333]
 * }, esignatureObj)
 *
 * Note: Requires e-signature validation for compliance.
 * Endpoints: POST /api/etmf/folderstructures/{countries|sites|trials}
 */
export const addMDtoMD = async (
  type: string,
  data: Record<string, any>,
  esignature?: Record<string, any>
): Promise<any> => {
  const requestObj = { ...data, esignature }

  // Map type to endpoint URL segment
  let typeUrl = 'trials' // default
  if (type === 'COUNTRY') {
    typeUrl = 'countries'
  } else if (type === 'SITE') {
    typeUrl = 'sites'
  }

  return baseApiService.fetch<any>(`/api/etmf/folderstructures/${typeUrl}`, {
    method: 'POST',
    body: requestObj
  })
}

/**
 * Removes master data (trials, countries, sites) from existing master data in ETMF structure.
 * Direct migration from AngularJS api.etmf.js (lines 735-752).
 *
 * This method is used to dissociate master data entities (e.g., remove countries from a trial,
 * or sites from a country) in the ETMF folder structure hierarchy.
 *
 * @param type - Master data type from Constants.etmf.masterData.type:
 *   - 'COUNTRY': Remove countries from a trial
 *   - 'SITE': Remove sites from a country
 *   - Default: 'TRIAL' (for other trial-level operations)
 * @param data - Master data configuration containing:
 *   - parentFsiId: Parent folder structure instance ID
 *   - childFsiIds: Array of child FSI IDs to remove
 *   - Additional configuration
 * @param esignature - Electronic signature object for validation (optional)
 * @returns Promise<any> - Result of the remove operation
 *
 * @example
 * // Remove sites from a country
 * await removeMDfromMD('SITE', {
 *   parentFsiId: 12345,
 *   childFsiIds: [111, 222]
 * }, esignatureObj)
 *
 * Note: Requires e-signature validation. Uses DELETE method with request body.
 * Endpoints: DELETE /api/etmf/folderstructures/{countries|sites|trials}
 */
export const removeMDfromMD = async (
  type: string,
  data: Record<string, any>,
  esignature?: Record<string, any>
): Promise<any> => {
  const requestObj = { ...data, esignature }

  // Map type to endpoint URL segment
  let typeUrl = 'trials' // default
  if (type === 'COUNTRY') {
    typeUrl = 'countries'
  } else if (type === 'SITE') {
    typeUrl = 'sites'
  }

  return baseApiService.fetch<any>(`/api/etmf/folderstructures/${typeUrl}`, {
    method: 'DELETE',
    body: requestObj
  })
}

/**
 * Direct migration of legacy getQcReviewReport from AngularJS (api.etmf.js)
 * @param filter - Filter object for QC review report
 * @param meta - Meta object for QC review report
 * @returns Promise resolving to QC review report data
 */
export async function getQcReviewReport(filter: Record<string, any>, meta: Record<string, any>): Promise<any> {
  const reqObj = { meta, ...filter }
  return baseApiService.fetch<any>('/api/reports/qcreview', {
    method: 'POST',
    body: reqObj
  })
}

/**
 * Direct migration of legacy exportQcReviewReport from AngularJS (api.etmf.js)
 * @param filter - Filter object for QC review report export
 * @param meta - Meta object for QC review report export
 * @returns Promise resolving to export response (file download or export status)
 */
export async function exportQcReviewReport(filter: Record<string, any>, meta?: Record<string, any>): Promise<any> {
  const reqObj = { meta, ...filter }
  return baseApiService.fetch<any>('/api/reports/qcreview/export', {
    method: 'POST',
    body: reqObj
  })
}

/**
 * Get country master data for update study dialog.
 * Used in update-study dialog to fetch countries that can be added/removed from an environment.
 * @param id - Environment FSI ID
 * @param params - Metadata filters for country selection
 * @param isAdd - Boolean indicating if this is for adding (true) or removing (false) countries
 * @param isSilent - If true, disables loader (default: false)
 * @returns Promise resolving to array of country master data objects
 */
export const getCountryMDsForUpdate = async (
  id: number | string,
  params: Array<{ tplId: string; vals?: string }>,
  isAdd: boolean,
  isSilent: boolean = false
): Promise<any[]> => {
  // Convert each filter object to JSON string for query params
  // Backend expects multiple 'metadataFilters' params, one for each filter object
  const queryParams: Record<string, any> = {
    add: isAdd
  }

  if (params && params.length > 0) {
    queryParams.metadataFilters = params.map((filter) => JSON.stringify(filter))
  }

  return baseApiService.fetch<any[]>(`/api/etmf/overview/studyupdatecountry/${id}`, {
    params: queryParams
  })
}
/*
 * Migrated from legacy AngularJS ApiEtmf.createQCReviewByFilters
 * POSTs to /api/workflows/createqcreviewbyfilters with the given DTO
 * @param dto - Request payload (filters, etc)
 * @returns Promise resolving to the API response
 */
export async function createQCReviewByFilters(dto: Record<string, any>): Promise<any> {
  // Legacy: showLoader: true (default behavior in baseApiService)
  return baseApiService.fetch<any>('/api/workflows/createqcreviewbyfilters', {
    method: 'POST',
    body: dto
  })
}

/**
<<<<<<< HEAD
 * Get site master data for update study dialog.
 * Used in update-study dialog to fetch sites that can be added/removed from an environment.
 * @param id - Environment FSI ID
 * @param params - Metadata filters for site selection
 * @param isAdd - Boolean indicating if this is for adding (true) or removing (false) sites
 * @param isSilent - If true, disables loader (default: false)
 * @returns Promise resolving to array of site master data objects (grouped by country)
 */
export const getSiteMDsForUpdate = async (
  id: number | string,
  params: Array<{ tplId: string; vals?: string }>,
  isAdd: boolean,
  isSilent: boolean = false
): Promise<any[]> => {
  // Convert each filter object to JSON string for query params
  // Backend expects multiple 'metadataFilters' params, one for each filter object
  const queryParams: Record<string, any> = {
    add: isAdd
  }

  if (params && params.length > 0) {
    queryParams.metadataFilters = params.map((filter) => JSON.stringify(filter))
  }

  return baseApiService.fetch<any[]>(`/api/etmf/overview/studyupdatesite/${id}`, {
    params: queryParams
  })
}

/**
 * Get site master data picklist filtered by selected countries.
 * Used in generate-study wizard Step 6 to show sites belonging to selected countries.
 * @param countryFsiIds - Array of country FSI IDs to filter sites by
 * @param params - Additional query parameters (sortBy, sortDesc, etc.)
 * @returns Promise resolving to array of site master data with country groupings
 */
export const getSiteMasterDataPicklist = async (
  countryFsiIds: Array<number | string>,
  params: Record<string, any> = {}
): Promise<any> => {
  const queryParams = {
    ...params,
    countryFsiIds: countryFsiIds.map(String)
  }
  return baseApiService.fetch<any>('/api/etmf/sitemasterdatapicklist', {
    params: queryParams
  })
}

/**
 * Delete ETMF files/folders with e-signature validation.
 * This is the ETMF-specific deletion API (different from files.deleteFile).
 * Used in update-study dialog to remove countries/sites and their associated folder structures.
 * @param fileId - Array of file/folder IDs to delete
 * @param validation - E-signature validation object
 * @returns Promise resolving to deletion response
 */
export const deleteFile = async (fileId: Array<number | string>, validation?: Record<string, any>): Promise<any> => {
  const requestObj: any = {
    ids: fileId
  }

  if (validation) {
    requestObj.esignature = validation
  }

  return baseApiService.fetch<any>('/api/etmf/remove/', {
    method: 'PATCH',
    body: requestObj
  })
}

/**
 * Search for instanced properties at a specific level in ETMF hierarchy
 * Returns dropdown options as key-value pairs (fsiName -> fsiId)
 *
 * @param level - ETMF level (PRODUCT, TRIAL, ENVIRONMENT, COUNTRY, SITE)
 * @param metadataFilters - Array of metadata filter objects with tplId and vals
 * @param isSilent - Optional flag to suppress loading indicator
 * @returns Promise resolving to array of {fsiId, fsiName} objects
 */
export const instancedPropsSearch = async (
  level: string,
  metadataFilters?: Array<{ tplId: string; vals?: string }>,
  isSilent?: boolean
): Promise<Array<{ fsiId: string; fsiName: string }>> => {
  // Convert each filter object to JSON string for query params
  // Backend expects multiple 'metadataFilters' params, one for each filter object
  const params: Record<string, any> = {}

  if (metadataFilters && metadataFilters.length > 0) {
    // Create array of JSON-stringified filter objects
    params.metadataFilters = metadataFilters.map((filter) => JSON.stringify(filter))
  }

  const response = await baseApiService.fetch<{ vals: Record<string, string> }>(
    `/api/etmf/overview/dropdown/${level}`,
    { params }
  )

  // Transform response from {vals: {fsiName: fsiId}} to [{fsiId, fsiName}]
  const result: Array<{ fsiId: string; fsiName: string }> = []
  if (response.vals) {
    Object.entries(response.vals).forEach(([fsiName, fsiId]) => {
      result.push({ fsiId, fsiName })
    })
  }

  return result
}

/*
 * Migrated from legacy AngularJS ApiEtmf.createQCReviewByIds
 * POSTs to /api/workflows/createqcreviewbyids with the given DTO
 * @param dto - Request payload (filters, etc)
 * @returns Promise resolving to the API response
 */
export async function createQCReviewByIds(dto: Record<string, any>): Promise<any> {
  // Legacy: showLoader: true (default behavior in baseApiService)
  return baseApiService.fetch<any>('/api/workflows/createqcreviewbyids', {
    method: 'POST',
    body: dto
  })
}

/**
 * Helper function to fetch raw entries from the API.
 * Called by getEntriesForSelector to retrieve file plan artifact tree structure.
 *
 * @param level - The entity level (e.g., 'STUDY', 'SITE', 'ENVIRONMENT')
 * @param filePlanId - The file plan ID
 * @param fsiId - The folder structure instance ID (contextId)
 * @param isSilent - If true, disables loader (default: true)
 * @param documentFsiId - Optional: Document FSI ID for clone scenarios
 * @param documentVersion - Optional: Document version for clone scenarios
 * @returns Promise resolving to raw API response with allArtifacts and predictions
 */
async function getEntries(
  level: string,
  filePlanId: string,
  fsiId: string | string[],
  isSilent: boolean = true,
  documentFsiId?: string,
  documentVersion?: string
): Promise<any> {
  // Build query params exactly as legacy does (order matters for parity)
  const params: Record<string, any> = {}

  if (fsiId) {
    params.fsiIds = fsiId
  }
  if (filePlanId) {
    params.fpId = filePlanId
  }
  if (documentFsiId) {
    params.documentFsiId = documentFsiId
  }
  if (documentVersion) {
    params.documentVersion = documentVersion
  }

  // showLoader: !isSilent (legacy pattern)
  return baseApiService.fetch<any>(`/api/v2/etmf/overview/selection/${level}/artifact`, { params })
}

/**
 * TypeScript interface for tree nodes at different levels
 */
interface TreeNodeBase {
  level: number
  expanded: boolean
  wasExpanded: boolean
  selected: boolean
  hasChildren: boolean
  name: string
  identifier: string
  containsSelected?: number
  containsContaining?: number
}

interface TreeNodeLevel3 extends TreeNodeBase {
  level: 3
  id: string
  isArtifact: true
  parent: TreeNodeLevel2
  fileName?: string
  metadataProperties?: any
}

interface TreeNodeLevel2 extends TreeNodeBase {
  level: 2
  parent: TreeNodeLevel1
  children: TreeNodeLevel3[]
}

interface TreeNodeLevel1 extends TreeNodeBase {
  level: 1
  children: TreeNodeLevel2[]
}

interface PredictedNode extends TreeNodeBase {
  level: 1
  isPredicted: true
  class?: string
  confidence?: number
  artifacts?: any[]
  children: Array<{
    level: 2
    expanded: boolean
    isPredicted: true
    identifier: string
    isArtifact: true
    selected: boolean
    hasChildren: false
    parent: PredictedNode
  }>
}

interface EntriesForSelectorResponse {
  all: TreeNodeLevel1[]
  predicted: PredictedNode[] | null
}

/**
 * Fetches file plan entries structured as a tree hierarchy (Zone → Section → Artifact).
 * Used by ArtifactSelector component to display artifact selection tree.
 *
 * Transforms flat API response into hierarchical structure with:
 * - Level 1: Zones
 * - Level 2: Sections
 * - Level 3: Artifacts
 *
 * Also handles AI-predicted artifacts with confidence scores.
 *
 * @param level - The entity level (e.g., 'STUDY', 'SITE', 'ENVIRONMENT')
 * @param filePlanId - The file plan ID
 * @param fsiId - The folder structure instance ID (contextId)
 * @param isSilent - If true, disables loader (default: true)
 * @param selectedIds - Array of currently selected artifact IDs (for preserving selection state)
 * @param documentFsiId - Optional: Document FSI ID for clone scenarios
 * @param documentVersion - Optional: Document version for clone scenarios
 * @returns Promise resolving to { all: TreeNode[], predicted: PredictedNode[] }
 */
export async function getEntriesForSelector(
  level: string,
  filePlanId: string,
  fsiId: string | string[],
  isSilent: boolean = true,
  selectedIds: string[] = [],
  documentFsiId?: string,
  documentVersion?: string
): Promise<EntriesForSelectorResponse> {
  // Build selectedMap for O(1) lookup (legacy pattern)
  const selectedMap = reduce(
    selectedIds || [],
    (mem: Record<string, string>, id: string) => {
      mem[id] = id
      return mem
    },
    {}
  )

  // Fetch raw entries from API
  const resp = await getEntries(level, filePlanId, fsiId, isSilent, documentFsiId, documentVersion)
  const respEntries = resp // Legacy uses .plain() which is no-op in our case
  const entriesL_1 = respEntries.allArtifacts

  // Transform Level 1 entries (Zones)
  const all = map(entriesL_1, (entriesL_2: any, nameL1: string) => {
    const entityL1: any = {
      level: 1,
      containsContaining: 0
    }

    // Transform Level 2 entries (Sections)
    entriesL_2 = map(entriesL_2, (entriesL_3: any, nameL2: string) => {
      const entityL2: any = {
        level: 2,
        containsContaining: 0
      }

      // Transform Level 3 entries (Artifacts)
      entriesL_3 = map(entriesL_3, (entryL_3: any) => {
        const isSelected = !!selectedMap[entryL_3.id]

        return extend(entryL_3, {
          level: 3,
          selected: isSelected,
          hasChildren: false,
          parent: entityL2,
          isArtifact: true,
          identifier: entryL_3.id
        })
      })

      if (entityL2.containsContaining > 0) {
        entityL1.containsContaining++
      }

      const selectedLev3 = where(entriesL_3, { selected: true })
      const expandedL2 = selectedLev3.length > 0

      return extend(entityL2, {
        expanded: expandedL2,
        wasExpanded: expandedL2,
        selected: entriesL_3 && entriesL_3.length > 0 && selectedLev3 && selectedLev3.length === entriesL_3.length,
        containsSelected: selectedLev3.length,
        containsContaining: selectedLev3.length,
        hasChildren: entriesL_3.length > 0,
        parent: entityL1,
        name: nameL2,
        identifier: nameL1 + '_' + nameL2,
        children: entriesL_3
      })
    })

    const selectedLev2 = where(entriesL_2, { selected: true })
    const expandedLev2 = where(entriesL_2, { expanded: true })
    const containingLev2 = filter(entriesL_2, (entry: any) => entry.containsContaining > 0)
    const expandedL1 = selectedLev2.length > 0 || expandedLev2.length > 0

    return extend(entityL1, {
      expanded: expandedL1,
      wasExpanded: expandedL1,
      selected: entriesL_2 && entriesL_2.length > 0 && selectedLev2 && selectedLev2.length === entriesL_2.length,
      containsSelected: selectedLev2.length,
      containsContaining: containingLev2.length,
      hasChildren: entriesL_2.length > 0,
      name: nameL1,
      identifier: nameL1,
      children: entriesL_2
    })
  })

  // Helper function to create children for predicted nodes
  function getChildrensForPredictedNode(artifacts: any[], entries: any) {
    return artifacts.map((item) => ({
      ...item,
      level: 2,
      expanded: false,
      isPredicted: true,
      identifier: item.id,
      isArtifact: true,
      selected: false,
      hasChildren: false,
      parent: entries
    }))
  }

  // Transform predicted artifacts if present
  let predicted: PredictedNode[] | null = null

  if (respEntries.predictions && isArray(respEntries.predictions)) {
    predicted = respEntries.predictions.reduce((acc: any[], item: any, index: number) => {
      const nodeName = `Predicted Artifact class: ${item.class}(${Math.round(item.confidence)}% Confidence)`

      const entries: any = {
        ...item,
        level: 1,
        expanded: false,
        selected: false,
        wasExpanded: false,
        isPredicted: true,
        hasChildren: item.artifacts && isArray(item.artifacts) ? !!item.artifacts.length : null,
        identifier: `${nodeName}_${index}`,
        name: nodeName
      }

      entries.children = getChildrensForPredictedNode(item.artifacts, entries)
      acc.push(entries)
      return acc
    }, [])
  }

  return {
    all,
    predicted
  }
}

/**
 * Retrieves clinical folder ID based on hierarchical ETMF parameters.
 * Matches legacy AngularJS ApiEtmf.getClinicalFolderIdByParams.
 * @param params - Object containing ETMF hierarchy (product, trial, environment, country, site, zone, section)
 * @returns Promise resolving to object with id property
 */
export async function getClinicalFolderIdByParams(params: ClinicalFolderParams): Promise<ClinicalFolderResponse> {
  return baseApiService.fetch<ClinicalFolderResponse>('/api/etmf/folderid', { params })
}

/**
 * Fetches "Add to Group" FSI options for a selected section/artifact combination.
 * Matches legacy AngularJS ApiEtmf.getAddToGroupFsi.
 * @param sectionId - The section FSI ID (optional)
 * @param artifactName - The artifact name (optional)
 * @param fsiId - The FSI ID (optional)
 * @param isSilent - If true, disables loader (default: true)
 * @returns Promise resolving to array of {id, name} objects
 */
export async function getAddToGroupFsi(
  sectionId?: number | string | Array<number | string>,
  artifactName?: string,
  fsiId?: number | string,
  isSilent: boolean = true
): Promise<GroupFsiItem[]> {
  const params: Record<string, any> = {
    ...(sectionId && Array.isArray(sectionId) && sectionId[0] && { sectionFsiId: sectionId[0] }),
    ...(artifactName && { artifact: artifactName }),
    ...(fsiId && { fsiId })
  }

  return baseApiService.fetch<GroupFsiItem[]>('/api/v1/etmf/fsi_group/selection/effectivefsi', {
    params
  })
}

/**
 * Response item from getSelectionsForFileToEtmf API call.
 */
export interface EtmfSelectionItem {
  fsiId: number
  fsiName: string
}

/**
 * Fetches selection options for File to ETMF dropdown cascade (product, study, environment, country, site, zone, section).
 * Matches legacy AngularJS ApiEtmf.getSelectionsForFileToEtmf.
 * @param level - The entity level (e.g., 'PRODUCT', 'STUDY', 'ENVIRONMENT', 'COUNTRY', 'SITE', 'ZONE', 'SECTION')
 * @param fsiId - The parent FSI ID for hierarchical filtering (optional)
 * @param isSilent - If true, disables loader (default: undefined -> shows loader). Legacy behavior: _.isBoolean(isSilent) ? !isSilent : isSilent
 * @returns Promise resolving to array of {fsiId, fsiName}
 */
export async function getSelectionsForFileToEtmf(
  level: string,
  fsiId?: number | string | Array<number | string>,
  isSilent?: boolean
): Promise<EtmfSelectionItem[]> {
  const params: Record<string, any> = {}
  if (fsiId !== undefined && fsiId !== null) {
    if (Array.isArray(fsiId)) {
      params.fsiIds = fsiId
    } else {
      params.fsiId = fsiId
    }
  }

  // Legacy loader logic: showLoader = _.isBoolean(isSilent) ? !isSilent : isSilent
  // If isSilent is explicitly boolean, invert it for showLoader
  // If isSilent is undefined/null, use default showLoader behavior (true)
  // For our implementation: if isSilent is false, show loader; if true, hide loader; if undefined, show loader
  const showLoader = isSilent === undefined ? true : !isSilent

  const response = await baseApiService.fetch<Array<{ id: number; name: string }>>(
    `/api/etmf/overview/selection/${level}`,
    { params }
  )

  return response.map((item) => ({
    fsiId: item.id,
    fsiName: item.name
  }))
}

/**
 * Response from getEnvironmentLevels API call.
 * Contains metadata template IDs for each ETMF hierarchy level.
 */
export interface EnvironmentLevelsResponse {
  level?: { id: number | string }
  product?: { id: number | string }
  trial?: { id: number | string }
  environment?: { id: number | string }
  country?: { id: number | string }
  site?: { id: number | string }
  zone?: { id: number | string }
  section?: { id: number | string }
  LEVEL?: { id: number | string }
  PRODUCT?: { id: number | string }
  TRIAL?: { id: number | string }
  ENVIRONMENT?: { id: number | string }
  COUNTRY?: { id: number | string }
  SITE?: { id: number | string }
  ZONE?: { id: number | string }
  SECTION?: { id: number | string }
}

/**
 * Retrieves metadata template IDs for all ETMF hierarchy levels.
 * Matches legacy AngularJS ApiEtmf.getEnvironmentLevels.
 * @param useUppercase - If true, transforms all keys to uppercase (default: false)
 * @returns Promise resolving to object with metadata template IDs for each level
 */
export async function getEnvironmentLevels(useUppercase: boolean = false): Promise<EnvironmentLevelsResponse> {
  const data = await getLinkedEntities()

  if (!data || !data.entityLinkMap) {
    throw new Error('Failed to get linked entities: entityLinkMap is missing')
  }

  const entityLinkMap = data.entityLinkMap

  let envLevels: any = {
    level: { id: entityLinkMap.LEVEL },
    product: { id: entityLinkMap.PRODUCT },
    trial: { id: entityLinkMap.TRIAL },
    environment: { id: entityLinkMap.ENVIRONMENT },
    country: { id: entityLinkMap.COUNTRY },
    site: { id: entityLinkMap.SITE },
    zone: { id: entityLinkMap.ZONE },
    section: { id: entityLinkMap.SECTION }
  }

  if (useUppercase) {
    const uppercasedLevels: any = {}
    Object.keys(envLevels).forEach((key) => {
      uppercasedLevels[key.toUpperCase()] = envLevels[key]
    })
    envLevels = uppercasedLevels
  }

  return envLevels as EnvironmentLevelsResponse
}

/*
 * Verify formatting for eTMF Clinical File Plan documents
 * Validates document structure and format against eTMF requirements
 * @param fsiId - The FSI ID of the document to verify formatting
 * @returns Promise resolving to validation result
 */
export async function verifyFormatting(fsiId: number | string): Promise<any> {
  return baseApiService.fetch<any>(`/api/etmf/fileplan/validate/${fsiId}`)
}

/**
 * Interface for Generate FST (Folder Structure Template) request
 */
export interface GenerateFSTRequest {
  name: string
  core: boolean
  device: boolean
  excelTemplateFsiId: number
  targetFolderFsiId: number
  product: boolean
  study: boolean
  environment: boolean
  country: boolean
  recommended: boolean
  site: boolean
  folderOnly: boolean
  esignature?: Record<string, unknown>
}

/**
 * Migrated from legacy AngularJS ApiEtmf.generateFSTpls
 * POSTs to /api/etmf/folderstructures/template to generate folder structure templates
 * @param data - Request payload for FST generation
 * @param esignature - Optional e-signature validation
 * @returns Promise resolving to the API response
 */
export async function generateFSTpls(
  data: Omit<GenerateFSTRequest, 'esignature'>,
  esignature?: Record<string, unknown>
): Promise<any> {
  const requestObj = { ...data, esignature }
  return baseApiService.fetch<any>('/api/etmf/folderstructures/template', {
    method: 'POST',
    body: requestObj
  })
}

/**
 * Fetches artifacts for a file plan.
 * Migrated from: app/api/api.etmf.js getArtifacts
 * @param filePlanId - The file plan ID
 * @returns Promise resolving to array of artifacts
 */
export async function getArtifacts(filePlanId: number | string): Promise<any[]> {
  return baseApiService.fetch<any[]>(`/api/etmf/getartifacts/${filePlanId}`)
}

/**
 * Enables artifacts for a file plan.
 * Migrated from: app/api/api.etmf.js enableArtifacts
 * @param filePlanId - The file plan ID
 * @param artifactIndices - Array of artifact indices to enable
 * @param esignature - E-signature validation object
 * @returns Promise resolving to API response
 */
export async function enableArtifacts(
  filePlanId: number | string,
  artifactIndices: number[],
  esignature?: Record<string, unknown>
): Promise<any> {
  const requestObj = {
    indices: artifactIndices,
    esignature: esignature
  }
  return baseApiService.fetch<any>(`/api/v1/etmf/fileplan/${filePlanId}/enableartifacts`, {
    method: 'POST',
    body: requestObj
  })
}

/**
 * Disables artifacts for a file plan.
 * Migrated from: app/api/api.etmf.js disableArtifacts
 * @param filePlanId - The file plan ID
 * @param artifactIndices - Array of artifact indices to disable
 * @param esignature - E-signature validation object
 * @returns Promise resolving to API response
 */
export async function disableArtifacts(
  filePlanId: number | string,
  artifactIndices: number[],
  esignature?: Record<string, unknown>
): Promise<any> {
  const requestObj = {
    indices: artifactIndices,
    esignature: esignature
  }
  return baseApiService.fetch<any>(`/api/v1/etmf/fileplan/${filePlanId}/disableartifacts`, {
    method: 'POST',
    body: requestObj
  })
}

/**
 * Staging area flags response interface
 * Contains permissions and capabilities for a specific staging area folder
 */
export interface StagingAreaFlags {
  canEditBlinded: boolean
  allowCertifiedCopies: boolean
  allowBlindingStatusChange: boolean
}

/**
 * Get staging area flags by FSI ID
 * Returns permissions and capabilities for a specific staging area folder
 *
 * Matches legacy AngularJS ApiEtmf.getStagingAreaFlagsByFsiId
 * @param fsiId - The FSI ID of the staging area folder
 * @returns Promise resolving to staging area flags
 */
export async function getStagingAreaFlagsByFsiId(fsiId: number | string): Promise<StagingAreaFlags> {
  const response = await baseApiService.fetch<StagingAreaFlags>(`/api/v1/etmf/stagingArea/${fsiId}`)
  return response
}

/**
 * Creates a master data file in ETMF.
 * Migrated from: app/api/api.etmf.js createMasterDataFile
 * @param type - Master data type (e.g., 'PRODUCT', 'ENVIRONMENT', 'TRIAL', 'COUNTRY', 'SITE')
 * @param name - Name of the master data file
 * @param esignature - E-signature validation object
 * @param params - Additional query parameters (e.g., countryName for COUNTRY type)
 * @returns Promise resolving to the created master data response with elements array
 */
export async function createMasterDataFile(
  type: MasterDataTypeValue,
  name: string,
  esignature?: Record<string, unknown>,
  params?: Record<string, any>
): Promise<{ elements: Array<{ id: number; [key: string]: any }> }> {
  const queryParams: Record<string, any> = { name }

  if (params && typeof params === 'object') {
    Object.assign(queryParams, params)
  }

  return baseApiService.fetch<{ elements: Array<{ id: number; [key: string]: any }> }>(`/api/etmf/masterdata/${type}`, {
    method: 'POST',
    params: queryParams,
    body: esignature || {}
  })
}

/**
 * Get master data instances for a specific master data file
 */
export async function getMasterDataInstances(
  masterDataId: number,
  params?: Record<string, any>
): Promise<Array<{ fsiId: number; path: string; [key: string]: any }>> {
  return baseApiService.fetch<Array<{ fsiId: number; path: string; [key: string]: any }>>(
    `/api/etmf/masterdatainstances/${masterDataId}`,
    { params }
  )
}

/**
 * Update master data properties on instances
 */
export async function updateMasterDataPropertiesOnInstances(
  params: {
    masterDatafsiId: number
    instantiatedFsiIds: number[]
  },
  esignature?: Record<string, unknown>
): Promise<void> {
  const requestObj = {
    ...params,
    esignature
  }

  return baseApiService.fetch<void>('/api/etmf/folderstructures/metadata', {
    method: 'PUT',
    body: requestObj
  })
}

/**
 * Timeliness report filter payload interface
 */
export interface TimelinessReportPayload {
  level: string
  product: string | null
  studies: string[]
  environment: string | null
  country: string | null
  site: string | null
  tags: number[]
  overdue: boolean
  dueSoon: boolean
  daysUntilDue: number | null
  placeholder: boolean
  document: boolean
  fsiIds: number[]
  [key: string]: any // Index signature for flexibility
}

/**
 * Timeliness report metrics response interface
 */
export interface TimelinessMetricsResponse {
  tagName?: string
  tagNames?: string[]
  totalPhs?: number
  totalDocs?: number
  overduePhs?: number
  timelinessRate?: number | string
}

/**
 * Timeliness report row interface
 */
export interface TimelinessReportRow {
  fsiName?: string
  studyName?: string
  countryName?: string
  siteName?: string
  zone?: string
  section?: string
  artifact?: string
  dueDate?: string
  status?: string
  daysUntilDue?: number
  daysOverdue?: number
}

/**
 * Timeliness report response interface
 */
export interface TimelinessReportResponse {
  metricsResponseDTO: TimelinessMetricsResponse
  result: {
    hasNextPage: boolean
    elements: TimelinessReportRow[]
  }
}

/**
 * Get ETMF timeliness report
 * Returns placeholder/document status with due dates
 *
 * @param filter - Filter payload containing level, FSI selections, tags, etc
 * @param meta - Meta parameters for pagination and sorting (embedded in request body)
 * @returns Promise resolving to timeliness report data
 */
export async function getTimelinessReport(
  filter: TimelinessReportPayload,
  meta: Record<string, any>
): Promise<TimelinessReportResponse> {
  // Legacy API expects meta embedded in the POST body, not as query params
  const requestBody = {
    ...filter,
    meta: meta
  }

  return baseApiService.fetch<TimelinessReportResponse>('/api/reports/etmftimeliness', {
    method: 'POST',
    body: requestBody
  })
}

/**
 * Export ETMF timeliness report
 * Triggers export generation and notification
 *
 * @param reportType - Report type identifier (e.g., 'ETMF_TIMELINESS')
 * @param filter - Filter payload
 * @param filterId - Optional saved filter ID
 * @returns Promise resolving to export response
 */
export async function exportTimelinessReport(
  reportType: string,
  filter: TimelinessReportPayload,
  filterId?: number
): Promise<any> {
  const payload: any = { ...filter }
  if (filterId && filterId !== -1) {
    payload.filterId = filterId
  }

  return baseApiService.fetch<any>('/api/reports/etmftimeliness/export', {
    method: 'POST',
    body: payload
  })
}

export async function fetchEMail(parentFsiId: string | number): Promise<void> {
  return baseApiService.fetch<void>(`/api/etmfemail/${parentFsiId}`, {
    method: 'GET'
  })
}

/**
 * Response interface for getSimFileId
 */
export interface SimFileIdResponse {
  id?: number
  fileId?: number
  [key: string]: unknown
}

/**
 * Get SIM (Site Initiation Monitoring) file ID by visit UUID, visit type category, and document type.
 * This is used to redirect users to view a specific SIM file.
 *
 * Parity with legacy ApiEtmf.getSimFileId
 *
 * @param visitUuid - Visit UUID identifier
 * @param visitTypeCategory - Visit type category
 * @param documentType - Document type
 * @returns Promise resolving to file ID response
 */
export async function getSimFileId(
  visitUuid: string,
  visitTypeCategory: string,
  documentType: string
): Promise<SimFileIdResponse> {
  return baseApiService.fetch<SimFileIdResponse>(
    `/api/v1/etmf/showSimFile?visitUuid=${encodeURIComponent(visitUuid)}&&visitTypeCategory=${encodeURIComponent(visitTypeCategory)}&&documentType=${encodeURIComponent(documentType)}`
  )
}
